import type {
  AgBarSparklinePreset,
  AgSparklineOptions
} from "ag-charts-community";
import type {
  CellRendererSelectorResult,
  ICellRendererParams
} from "ag-grid-enterprise";

/**
 * `padding` is documented for sparklines and the runtime validates it
 * (`commonSparklineOptionsDef`), but the preset types omit it. Declared here so
 * it survives type-checking — without it the bars sit flush against the cell
 * edges. Remove once `AgSparklineOptions` carries it.
 */
interface SparklinePaddingWorkaround {
  padding?: { top?: number; right?: number; bottom?: number; left?: number };
}

/**
 * What `agSparklineCellRenderer` takes. Typed against the library rather than
 * inferred, so an option AG renames or drops fails the build instead of being
 * silently ignored at runtime — `cellRendererParams` is otherwise untyped.
 */
export interface SparklineCellParams<
  TOptions extends AgSparklineOptions = AgBarSparklinePreset
> {
  /** Draw only once the cell is on screen, not for every row created. */
  deferRender: true;
  sparklineOptions: TOptions & SparklinePaddingWorkaround;
}

/**
 * A cell value as the series a sparkline draws, or null when it is none.
 *
 * A column with no value getter hands the raw field through, and the wire
 * JSON-encodes these arrays — so `"[]"` has to read as empty rather than as a
 * non-empty string. Anything that is not a series after that is null: a scalar
 * belongs to a variant row, which a `cellRendererSelector` hands to the text
 * renderer.
 */
export function parseSeries(value: unknown): unknown[] | null {
  if (value == null) return null;
  const series = typeof value === "string" ? tryParse(value) : value;
  return Array.isArray(series) ? series : null;
}

function tryParse(value: string): unknown {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

// One wrapper per params object: AG Grid compares the selector's result against
// the previous one, so a fresh `{ component, params }` per cell would re-resolve
// the component on every render.
const cellByParams = new WeakMap<object, CellRendererSelectorResult>();

/**
 * Wires `agSparklineCellRenderer`, but **only** where there is a series.
 *
 * A value getter returning null does not spare the work: AG Grid decides a cell
 * has a renderer from the column definition alone, and the renderer builds a
 * chart for an empty dataset just the same. Withholding the component here is
 * what skips it — and every chart withheld is one fewer in the process-wide
 * sparkline pool that all these columns share.
 *
 * `paramsFor` runs per cell, so it must return a cached object rather than
 * building one.
 */
export function sparklineSelector<TOptions extends AgSparklineOptions>(
  paramsFor: (params: ICellRendererParams) => SparklineCellParams<TOptions>
): (params: ICellRendererParams) => CellRendererSelectorResult | undefined {
  return function selectSparkline(params) {
    if (!parseSeries(params.value)?.length) return undefined;

    const rendererParams = paramsFor(params);
    let cell = cellByParams.get(rendererParams);
    if (!cell) {
      cell = { component: "agSparklineCellRenderer", params: rendererParams };
      cellByParams.set(rendererParams, cell);
    }
    return cell;
  };
}
